import { Box, Button, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import IconifyIcon from 'components/base/IconifyIcon';
import { useAuth } from 'providers/AuthProvider';

/**
 * Persistent bar shown while a developer impersonation session is active
 * (api/scripts/impersonate.mjs). Self-hides otherwise.
 *
 * This is the one thing standing between "debugging a couple's gallery" and
 * "editing a real wedding believing it's your test event" — impersonation runs
 * with writes live. It renders only from the impersonating tab's own
 * sessionStorage, so the couple can never see it.
 *
 * Deliberately untranslated: it is developer tooling that no user ever reaches,
 * and adding en/el keys for it would imply otherwise.
 *
 * Exit goes through `exitSandbox` — impersonation reuses the sandbox per-tab
 * storage scope (see utils/sessionStore.ts), so leaving is the same operation:
 * drop the scope, hard-reload into the real session. It must NEVER call
 * `endSandbox()`, which asks the server to delete the sandbox instance; that
 * would be meaningless here (the API rejects it — `requireSandboxCouple` checks
 * `isSandbox` on the database record, not the token — but the intent is wrong).
 */
const ImpersonationBanner = () => {
  const { sessionUser, exitSandbox } = useAuth();

  if (!sessionUser?.isImpersonating) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'sticky',
        // Same offset as SandboxBanner: pinned flush beneath the fixed AppBar so
        // the two bars never overlap on scroll (64px on xs/sm, 82px from md up —
        // the `appbar` Toolbar variant height).
        top: { xs: 64, md: 82 },
        zIndex: (theme) => theme.zIndex.appBar + 1,
        bgcolor: 'error.main',
        // `common.white`, not `error.contrastText`: the light palette defines no
        // contrastText on the error channel (only the dark one does — see
        // theme/palette/lightPalette.ts), and alpha() throws on an undefined
        // color. White reads correctly on error.main in both schemes anyway.
        color: 'common.white',
        px: { xs: 2, md: 3 },
        py: { xs: 1.5, md: 2 },
      }}
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <IconifyIcon
            icon="solar:danger-triangle-bold-duotone"
            sx={{ fontSize: 24, flexShrink: 0 }}
          />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Impersonating a real couple — writes are live
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                opacity: 0.9,
                lineHeight: 1.35,
                pt: 1,
                wordBreak: 'break-word',
              }}
            >
              {sessionUser.email}
              {sessionUser.eventId ? ` · ${sessionUser.eventId}` : ''}
            </Typography>
          </Box>
        </Stack>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={() => exitSandbox()}
          sx={{
            flexShrink: 0,
            fontWeight: 700,
            borderRadius: 999,
            borderColor: (theme) => alpha(theme.palette.common.white, 0.5),
            '&:hover': {
              borderColor: 'common.white',
              bgcolor: (theme) => alpha(theme.palette.common.white, 0.12),
            },
          }}
        >
          Exit
        </Button>
      </Stack>
    </Box>
  );
};

export default ImpersonationBanner;
